import { api } from '@/lib/api';
import type {
  SimCard,
  SimCardSummary,
  SimCardsResponse,
  SimCardFilters,
  AssignAccountInput,
  LinkTransmitterInput,
  SimCardMatchStats,
  AutoMatchResult,
  TransmitterDetails,
  MatchCandidate,
  SimSubscriptionInfo,
  ManagedSimCard,
  ManagedSimCardsResponse,
} from './types';

// =============================================================================
// V4 API Response Types (camelCase from backend)
// =============================================================================

interface V4SimCard {
  id: string;
  iccid: string;
  imei: string | null;
  msisdn: string | null;
  imsi: string | null;
  jasperDeviceId: string | null;
  jasperAccountId: string | null;
  jasperCustomer: string | null;
  status: string;
  jasperStatus: string | null;
  dateActivated: string | null;
  dateShipped: string | null;
  ratePlan: string | null;
  ratePlanId?: string | null;
  communicationPlan: string | null;
  usageCtdBytes: number;
  usageCtdMb: number | string;
  smsUsageCtd: number;
  dataSessionCount: number;
  lastSessionIp: string | null;
  lastSessionStart: string | null;
  lastSessionEnd: string | null;
  accountId: string | null;
  mongoTransmitterId: string | null;
  mongoGatewayId: string | null;
  matchMethod: string | null;
  lastSyncAt: string | null;
  syncStatus: string | null;
  syncError?: string | null;
  createdAt: string;
  updatedAt: string;
  // Joined account info
  accountName: string | null;
  accountNumber: string | null;
  hasActiveContract?: boolean;
}

interface V4SimCardsResponse {
  success: boolean;
  simCards: V4SimCard[];
  total: number;
  pageNumber: number;
  pageLength: number;
}

interface V4SimCardResponse {
  success: boolean;
  simCard: V4SimCard;
}

interface V4SimCardsSummaryResponse {
  success: boolean;
  summary: {
    total: number;
    active: number;
    inactive: number;
    suspended: number;
    inventory: number;
    test: number;
    retired: number;
    unknown: number;
    unassigned: number;
    totalUsageMb: string;
    // Cost calculation
    monthlyCost: string;
    totalPoolMb: string;
    volumeTier: 'tier1' | 'tier2' | 'tier3';
    unpricedSimCount: number;
  };
}

// =============================================================================
// Transform Functions
// =============================================================================

function transformSimCard(v4: V4SimCard): SimCard {
  return {
    id: v4.id,
    iccid: v4.iccid,
    imei: v4.imei,
    msisdn: v4.msisdn,
    imsi: v4.imsi,
    jasper_device_id: v4.jasperDeviceId,
    jasper_account_id: v4.jasperAccountId,
    jasper_customer: v4.jasperCustomer,
    status: v4.status as SimCard['status'],
    jasper_status: v4.jasperStatus,
    date_activated: v4.dateActivated,
    date_shipped: v4.dateShipped,
    rate_plan: v4.ratePlan,
    rate_plan_id: v4.ratePlanId || null,
    communication_plan: v4.communicationPlan,
    usage_ctd_bytes: v4.usageCtdBytes,
    usage_ctd_mb: typeof v4.usageCtdMb === 'string' ? parseFloat(v4.usageCtdMb) : v4.usageCtdMb,
    sms_usage_ctd: v4.smsUsageCtd,
    data_session_count: v4.dataSessionCount,
    last_session_ip: v4.lastSessionIp,
    last_session_start: v4.lastSessionStart,
    last_session_end: v4.lastSessionEnd,
    account_id: v4.accountId,
    mongo_transmitter_id: v4.mongoTransmitterId,
    mongo_gateway_id: v4.mongoGatewayId,
    match_method: v4.matchMethod,
    last_sync_at: v4.lastSyncAt,
    sync_status: v4.syncStatus,
    sync_error: v4.syncError || null,
    created_at: v4.createdAt,
    updated_at: v4.updatedAt,
    account_name: v4.accountName,
    account_number: v4.accountNumber,
    has_active_subscription: v4.hasActiveContract ?? undefined,
    has_active_contract: v4.hasActiveContract ?? undefined,
  };
}

function transformSummary(v4: V4SimCardsSummaryResponse['summary']): SimCardSummary {
  return {
    total: v4.total,
    active: v4.active,
    inactive: v4.inactive,
    suspended: v4.suspended,
    inventory: v4.inventory,
    test: v4.test,
    retired: v4.retired,
    unknown: v4.unknown,
    unassigned: v4.unassigned,
    total_usage_mb: v4.totalUsageMb,
    monthly_cost: v4.monthlyCost,
    total_pool_mb: v4.totalPoolMb,
    volume_tier: v4.volumeTier,
    unpriced_sim_count: v4.unpricedSimCount,
  };
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * Get SIM cards with optional filtering
 */
export async function getSimCards(filters: SimCardFilters = {}): Promise<SimCardsResponse> {
  const params: Record<string, string | number | boolean> = {};

  if (filters.search) params.search = filters.search;
  if (filters.status) params.status = filters.status;
  if (filters.jasper_status) params.jasperStatus = filters.jasper_status;
  if (filters.account_id) params.accountId = filters.account_id;
  if (filters.unassigned) params.unassigned = 'true';
  if (filters.matched !== undefined) params.matched = filters.matched ? 'true' : 'false';
  if (filters.page_number !== undefined) params.pageNumber = filters.page_number;
  if (filters.page_length !== undefined) params.pageLength = filters.page_length;
  if (filters.sort_key) params.sortKey = filters.sort_key;
  if (filters.sort_direction !== undefined) params.sortDirection = filters.sort_direction;

  const response = await api.get<V4SimCardsResponse>('/v4/sim-cards', params);

  return {
    sim_cards: response.simCards.map(transformSimCard),
    total: response.total,
    page_number: response.pageNumber,
    page_length: response.pageLength,
  };
}

/**
 * Get SIM card summary statistics
 */
export async function getSimCardsSummary(): Promise<SimCardSummary> {
  const response = await api.get<V4SimCardsSummaryResponse>('/v4/sim-cards/summary');
  return transformSummary(response.summary);
}

/**
 * Get every Telnyx SIM persisted by sims_api. The legacy endpoint caps pages
 * at 1,000 rows, so continue until the final partial page.
 */
export async function getTelnyxSimCards(): Promise<ManagedSimCard[]> {
  const pageLength = 1000;
  const simCards: ManagedSimCard[] = [];
  let pageNumber = 0;

  while (true) {
    const response = await api.get<ManagedSimCardsResponse>('/v4/legacy/sims', {
      provider: 'telnyx',
      pageNumber,
      pageLength,
      sortKey: 'iccid',
      sortDirection: 1,
      populate: JSON.stringify(['transmitter', 'gateway']),
    });
    simCards.push(...response.sims);

    if (response.sims.length < pageLength) break;
    pageNumber += 1;
  }

  return simCards;
}

/**
 * Get a single SIM card by ID
 */
export async function getSimCard(id: string): Promise<SimCard> {
  const response = await api.get<V4SimCardResponse>(`/v4/sim-cards/${id}`);
  return transformSimCard(response.simCard);
}

/**
 * Get a SIM card by ICCID
 */
export async function getSimCardByIccid(iccid: string): Promise<SimCard> {
  const response = await api.get<V4SimCardResponse>(`/v4/sim-cards/by-iccid/${iccid}`);
  return transformSimCard(response.simCard);
}

/**
 * Get SIM cards for an account
 */
export async function getSimCardsByAccount(accountId: string): Promise<SimCard[]> {
  const response = await api.get<{ success: boolean; simCards: V4SimCard[]; count: number }>(
    `/v4/sim-cards/by-account/${accountId}`
  );
  return response.simCards.map(transformSimCard);
}

/**
 * Assign a SIM card to an account
 */
export async function assignSimCardAccount(
  simCardId: string,
  input: AssignAccountInput
): Promise<SimCard> {
  const response = await api.post<V4SimCardResponse>(
    `/v4/sim-cards/${simCardId}/assign-account`,
    { accountId: input.account_id }
  );
  return transformSimCard(response.simCard);
}

/**
 * Link a SIM card to a transmitter
 */
export async function linkSimCardTransmitter(
  simCardId: string,
  input: LinkTransmitterInput
): Promise<SimCard> {
  const response = await api.post<V4SimCardResponse>(
    `/v4/sim-cards/${simCardId}/link-transmitter`,
    {
      transmitterId: input.transmitter_id,
      matchMethod: input.match_method || 'manual',
    }
  );
  return transformSimCard(response.simCard);
}

// =============================================================================
// Matching API Functions
// =============================================================================

interface V4MatchStatsResponse {
  success: boolean;
  stats: {
    totalSims: number;
    matchedSims: number;
    unmatchedSims: number;
    totalTransmitters: number;
    matchedTransmitters: number;
    unmatchedTransmitters: number;
  };
}

interface V4AutoMatchResponse {
  success: boolean;
  newlyMatched: number;
  unmatched: number;
}

interface V4TransmitterResponse {
  success: boolean;
  transmitter: {
    id: string;
    name: string | null;
    serial: string | null;
    model: string | null;
    manufacturer: string | null;
    organisation: string | null;
    lastPacketAt: string | null;
  };
}

interface V4MatchCandidatesResponse {
  success: boolean;
  candidates: Array<{
    transmitterId: string;
    transmitterName: string;
    matchMethod: string;
    confidence: 'high' | 'medium' | 'low';
  }>;
}

/**
 * Get SIM-to-transmitter match statistics
 */
export async function getMatchStats(): Promise<SimCardMatchStats> {
  const response = await api.get<V4MatchStatsResponse>('/v4/sim-cards/match-stats');
  return {
    total_sims: response.stats.totalSims,
    matched_sims: response.stats.matchedSims,
    unmatched_sims: response.stats.unmatchedSims,
    total_transmitters: response.stats.totalTransmitters,
    matched_transmitters: response.stats.matchedTransmitters,
    unmatched_transmitters: response.stats.unmatchedTransmitters,
  };
}

/**
 * Run auto-matching of SIM cards to transmitters
 */
export async function runAutoMatch(): Promise<AutoMatchResult> {
  const response = await api.post<V4AutoMatchResponse>('/v4/sim-cards/auto-match', {});
  return {
    newly_matched: response.newlyMatched,
    unmatched: response.unmatched,
  };
}

/**
 * Get transmitter details for a SIM card
 */
export async function getSimTransmitter(simCardId: string): Promise<TransmitterDetails | null> {
  try {
    const response = await api.get<V4TransmitterResponse>(`/v4/sim-cards/${simCardId}/transmitter`);
    return {
      id: response.transmitter.id,
      name: response.transmitter.name,
      serial: response.transmitter.serial,
      model: response.transmitter.model,
      manufacturer: response.transmitter.manufacturer,
      organisation: response.transmitter.organisation,
      last_packet_at: response.transmitter.lastPacketAt,
    };
  } catch {
    return null;
  }
}

/**
 * Get potential match candidates for an unmatched SIM card
 */
export async function getMatchCandidates(simCardId: string): Promise<MatchCandidate[]> {
  const response = await api.get<V4MatchCandidatesResponse>(
    `/v4/sim-cards/${simCardId}/match-candidates`
  );
  return response.candidates.map((c) => ({
    transmitter_id: c.transmitterId,
    transmitter_name: c.transmitterName,
    match_method: c.matchMethod,
    confidence: c.confidence,
  }));
}

// =============================================================================
// Contract API Functions
// =============================================================================

interface V4SimSubscriptionsResponse {
  success: boolean;
  accountId: string | null;
  accountName: string | null;
  accountNumber: string | null;
  subscriptions: Array<{
    id: string;
    subscriptionNumber: string;
    name: string;
    status: string;
    startDate: string | null;
    endDate: string | null;
    totalValue: string | null;
    currency: string;
  }>;
}

/**
 * Get contracts for a SIM card's linked account
 */
export async function getSimSubscriptions(simCardId: string): Promise<SimSubscriptionInfo> {
  const response = await api.get<V4SimSubscriptionsResponse>(`/v4/sim-cards/${simCardId}/subscriptions`);
  return {
    account_id: response.accountId,
    account_name: response.accountName,
    account_number: response.accountNumber,
    contracts: response.subscriptions.map((c) => ({
      id: c.id,
      contract_number: c.subscriptionNumber,
      name: c.name,
      status: c.status as SimSubscriptionInfo['contracts'][0]['status'],
      start_date: c.startDate,
      end_date: c.endDate,
      total_value: c.totalValue ? parseFloat(c.totalValue) : null,
      currency: c.currency,
    })),
  };
}

/** @deprecated Use getSimSubscriptions instead */
export const getSimContracts = getSimSubscriptions;
